import { useEffect, useRef, useState } from "react";

export type ChartPalette = {
  chart: string[];
  grid: string;
  ink: string;
  fill: string;
  inkMuted: string;
  inkStrong: string;
  brandInk: string;
  brand: string;
  ok: string;
  surface: string;
  border: string;
  hover: string;
};

function read(): ChartPalette {
  const root = document.documentElement;
  const cs = getComputedStyle(root);
  // jsdom does not reliably resolve a custom property set via style.setProperty
  // through getComputedStyle, so read the inline style first and fall back to
  // the computed style (what real browsers resolve for cascaded/themed tokens).
  const v = (name: string) => {
    const inline = root.style.getPropertyValue(name).trim();
    return inline || cs.getPropertyValue(name).trim();
  };
  return {
    chart: [1, 2, 3, 4, 5, 6].map((n) => v(`--chart-${n}`)),
    grid: v("--border"),
    ink: v("--ink-3"),
    fill: v("--fin-chart-fill"),
    inkMuted: v("--ink-4"),
    inkStrong: v("--ink"),
    brandInk: v("--brand-ink"),
    brand: v("--brand-primary"),
    ok: v("--ok"),
    surface: v("--surface"),
    border: v("--border"),
    hover: v("--hover"),
  };
}

/** Chart colours resolved from the current theme's tokens; updates when the
 * theme attribute on <html> changes. */
export function useChartPalette(): ChartPalette {
  const [palette, setPalette] = useState<ChartPalette>(read);
  // ThemeProvider rewrites data-theme with the same value on unrelated changes
  // (e.g. a system reduced-motion toggle while following the system theme);
  // skip the re-read when the attribute value hasn't actually changed.
  const lastTheme = useRef(document.documentElement.dataset.theme);
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const theme = document.documentElement.dataset.theme;
      if (theme === lastTheme.current) return;
      lastTheme.current = theme;
      setPalette(read());
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return palette;
}
